import bindBloomreachEvents from "../helpers/bloomreach";

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";
const DETAILS_SECTION_ID = "product-add-on-details";
const DETAILS_MODAL_ID = "add-on-modal";
const QUICK_ADD_MODAL_ID = "quick-add-modal";
const ERROR_DURATION = 3000;

/**
 * Product rail actions, for the add-on and also shown rails. On desktop the
 * image and a sized product's add to bag open the add-on details modal; below
 * that the image is a plain link and a sized product opens the quick add
 * sheet. Single-variant products add straight to the bag everywhere.
 */
class ProductAddOns extends HTMLElement {
  connectedCallback() {
    this.desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    this.addEventListener("click", this._onClick);
    this.addEventListener("pointerover", this._prefetch);
    this.addEventListener("focusin", this._prefetch);

    bindBloomreachEvents();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("pointerover", this._prefetch);
    this.removeEventListener("focusin", this._prefetch);
    clearTimeout(this._errorTimeout);
  }

  get _isDesktop() {
    return this.desktopQuery.matches;
  }

  // Warms the cache for whichever modal this trigger would open.
  _prefetch = (event) => {
    const details = event.target.closest("[data-add-on-details]");
    const quickAdd = event.target.closest("[data-quick-add-url]");

    if (details && this._isDesktop) {
      window
        .loadQuickAdd(details.dataset.addOnDetails, DETAILS_SECTION_ID)
        .catch(() => {});
    } else if (quickAdd) {
      const { url, sectionId } = this._quickAddTarget(quickAdd);
      window.loadQuickAdd(url, sectionId).catch(() => {});
    }
  };

  _onClick = (event) => {
    const details = event.target.closest("[data-add-on-details]");

    if (details) {
      // Below desktop, and for modifier-clicks, the image is just a link.
      if (!this._isDesktop) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      event.preventDefault();
      this._openModal(
        details,
        details.dataset.addOnDetails,
        DETAILS_SECTION_ID,
        DETAILS_MODAL_ID,
      );
      return;
    }

    const trigger = event.target.closest("[data-quick-add]");
    if (!trigger) return;

    if (trigger.dataset.quickAddVariant) {
      const { quickAddVariant, quickAddNote } = trigger.dataset;
      this._addToCart(trigger, quickAddVariant, quickAddNote);
    } else if (trigger.dataset.quickAddUrl) {
      const { url, sectionId, modalId } = this._quickAddTarget(trigger);
      this._openModal(trigger, url, sectionId, modalId);
    }
  };

  _quickAddTarget(trigger) {
    const url = trigger.dataset.quickAddUrl;

    return this._isDesktop
      ? { url, sectionId: DETAILS_SECTION_ID, modalId: DETAILS_MODAL_ID }
      : { url, sectionId: undefined, modalId: QUICK_ADD_MODAL_ID };
  }

  _openModal(trigger, url, sectionId, modalId) {
    const html = window.loadQuickAdd(url, sectionId);

    html.catch(() => window.location.assign(url));

    trigger.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { target: modalId, html },
      }),
    );
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} id Variant id
   * @param {string} [note] The product's promo tag `Note` line item property
   *   value, if it has one
   */
  _addToCart(trigger, id, note) {
    if (trigger.hasAttribute("aria-busy")) return;

    trigger.setAttribute("aria-busy", "true");

    window.addToCart(
      { id, quantity: 1, ...(note && { properties: { Note: note } }) },
      {
        onAfterAdd: () => {
          trigger.removeAttribute("aria-busy");
          document.dispatchEvent(new CustomEvent("mini-cart:open"));
        },
        onError: (response) => {
          trigger.removeAttribute("aria-busy");
          this._showError(
            trigger,
            response?.description ||
              response?.message ||
              window.localeStrings.product.add_to_cart_error,
          );
        },
      },
    );
  }

  _showError(trigger, message) {
    const error = trigger
      .closest(".product-rail__slide")
      ?.querySelector("[data-quick-add-error]");
    if (!error) return;

    this.querySelectorAll("[data-quick-add-error]").forEach((element) => {
      element.textContent = "";
    });
    error.textContent = message;

    clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => {
      error.textContent = "";
    }, ERROR_DURATION);
  }
}

if (!customElements.get("product-add-ons")) {
  customElements.define("product-add-ons", ProductAddOns);
}
